/**
 * Campaign statistics — per-campaign stat sheet + event timeline.
 *
 * Reachable at /statistics/:campaignId. Lifted out of Campaigns.jsx in
 * v10.19.8 so /campaigns stays a pure list and the stat sheet has its
 * own bookmarkable URL the operator can forward to the filmmaker.
 *
 * Side effect on mount: writes the current campaign id to
 * localStorage.activeCampaignId so the Dashboard's campaign widget
 * knows which one to show summary stats for.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Button, Grid, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ChevronLeft as BackIcon,
  Print as PrintIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { fmtDateTime, fmtDate } from '../utils/formatDate';
import CampaignPoster from '../components/CampaignPoster';
import CampaignEditDialog from '../components/CampaignEditDialog';


function StatTile({ label, value, hint }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="caption" sx={{
        display: 'block', color: 'text.secondary',
        textTransform: 'uppercase', letterSpacing: 0.4,
        fontSize: '0.65rem', fontWeight: 600,
      }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.25, color: 'primary.main' }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
          {hint}
        </Typography>
      )}
    </Paper>
  );
}


export function eventLabel(et) {
  return ({
    watermark_created: 'Screener encode created',
    watermark_completed: 'Screener encode completed',
    dcp_created: 'DCP created',
    dcp_completed: 'DCP completed',
    kdm_emailed: 'KDM emailed',
    share_created: 'Delivery link created',
    share_emailed: 'Delivery link emailed',
    share_downloaded: 'DCP downloaded',
    screener_emailed: 'Screener emailed',
    screener_downloaded: 'Screener downloaded',
    screener_watched: 'Screener watched',
    physical_staged: 'Drive staged',
    physical_shipped: 'Drive shipped',
    physical_delivered: 'Drive delivered',
    physical_received: 'Drive received at venue',
    physical_tested: 'Drive tested at venue',
    outcome_recorded: 'Outcome recorded',
  })[et] || et;
}


export function eventColor(et) {
  if (et.endsWith('_completed') || et.endsWith('_downloaded') || et.endsWith('_watched')
      || et === 'physical_delivered' || et === 'physical_received' || et === 'physical_tested') {
    return 'success.main';
  }
  if (et.startsWith('physical_') || et === 'share_emailed' || et === 'screener_emailed' || et === 'kdm_emailed') {
    return 'info.main';
  }
  return 'text.secondary';
}


export function payloadSummary(ev) {
  const p = ev.payload || {};
  const bits = [];
  if (p.recipient_name) bits.push(p.recipient_name);
  if (p.recipient_email && !p.recipient_name) bits.push(p.recipient_email);
  if (p.sent_to) bits.push(p.sent_to);
  if (p.venue_name) bits.push(p.venue_name);
  if (p.name) bits.push(p.name);
  if (p.tracking_number) bits.push(`${p.carrier || 'tracking'} ${p.tracking_number}`);
  if (p.outcome) bits.push(p.outcome.replace(/_/g, ' '));
  if (p.completion_percent != null) bits.push(`${Number(p.completion_percent).toFixed(0)}%`);
  return bits.join(' · ');
}


// v10.19.10: dual-mode — route component reads :campaignId from the
// URL, embedded component takes campaignId as a prop. Embedded mode
// (used by Dashboard) skips the back button + outer padding so it
// nests cleanly inside another page's layout.
export default function CampaignStatistics({ campaignId: propCampaignId, embedded = false } = {}) {
  const navigate = useNavigate();
  const params = useParams();
  const campaignId = propCampaignId || params.campaignId;
  const [campaign, setCampaign] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setError(null);
      const { data } = await api.get(`/api/campaigns/${campaignId}`);
      setCampaign(data);
      try { localStorage.setItem('activeCampaignId', String(data.id)); } catch (_e) { /* private mode */ }
    } catch (e) {
      setError(e?.response?.status === 404 ? 'Campaign not found' : 'Could not load campaign');
    }
  };

  useEffect(() => { if (campaignId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campaignId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/api/campaigns/${campaignId}/refresh`);
      await new Promise((r) => setTimeout(r, 1500));
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  if (error) {
    return (
      <Box sx={{ p: embedded ? 0 : { xs: 1.5, md: 3 } }}>
        <Typography color="text.secondary">{error}</Typography>
        {!embedded && (
          <Button sx={{ mt: 2 }} onClick={() => navigate('/campaigns')}>← Back to Campaigns</Button>
        )}
      </Box>
    );
  }
  if (!campaign) {
    return <Box sx={{ p: embedded ? 0 : 3 }}><CircularProgress size={20} /></Box>;
  }
  const s = campaign.summary || {};
  return (
    <Box sx={{ p: embedded ? 0 : { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        {!embedded && (
          <IconButton onClick={() => navigate('/campaigns')} size="small" sx={{ mt: 0.5 }}><BackIcon /></IconButton>
        )}
        {/* v10.19.15: hover the poster to surface upload/replace/clear
            affordances. Sized larger here than the list tile so the
            operator can verify the image looks right. */}
        <CampaignPoster
          campaignId={campaign.id}
          hasPoster={!!campaign.has_poster}
          orientation={campaign.poster_orientation}
          shortEdge={embedded ? 160 : 220}
          editable
          onUpload={load}
          onClear={load}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant={embedded ? 'h6' : 'h5'} sx={{ fontWeight: 700, flex: 1 }}>
          {campaign.name} <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400, fontSize: '0.7em' }}>· Statistics</Box>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {embedded && (
          <Tooltip title="Open the standalone statistics page (deep-linkable, forward to the filmmaker)">
            <Button size="small" variant="text" onClick={() => navigate(`/statistics/${campaign.id}`)}>
              Open standalone →
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Edit campaign (name, description, archive)">
          <IconButton size="small" onClick={() => setEditOpen(true)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Print / save as PDF for the filmmaker">
          <IconButton size="small" onClick={() => window.print()}>
            <PrintIcon />
          </IconButton>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          Refresh from sources
        </Button>
        </Box>
        </Box>
      </Box>

      {campaign.description && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', mb: 2 }}>
          {campaign.description}
        </Typography>
      )}

      {/* v10.19.20: producer-facing headline tiles. Counts that
          matter to a filmmaker tracking their release — how far the
          film traveled, not how many bytes / KDMs / drives we
          processed to get it there. */}
      <Typography variant="caption" sx={{
        display: 'block', mb: 1, color: 'text.secondary',
        textTransform: 'uppercase', letterSpacing: 0.4,
        fontSize: '0.7rem', fontWeight: 600,
      }}>Reach</Typography>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="DCP Deliveries"
            value={s.total_deliveries || 0}
            hint={`${s.dcps_delivered_to_venues || 0} DCP playback + ${s.venues_required_emergency_mp4 || 0} emergency MP4`}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="Theaters with DCP"
            value={s.dcps_delivered_to_venues || 0}
            hint="successfully played back"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="Required emergency MP4"
            value={s.venues_required_emergency_mp4 || 0}
            hint="KDM failed; shipped unencrypted"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="Screeners watched"
            value={s.screeners_watched_to_completion || 0}
            hint="≥95% complete"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="Screeners encoded"
            value={s.watermark_screeners_completed || 0}
            hint={`${s.bound_watermark_jobs || 0} total jobs`}
          />
        </Grid>
      </Grid>

      {/* v10.19.26: producer-facing watch engagement. How many people
          actually opened the screener, how long they watched, how
          deep they got. */}
      <Typography variant="caption" sx={{
        display: 'block', mb: 1, color: 'text.secondary',
        textTransform: 'uppercase', letterSpacing: 0.4,
        fontSize: '0.7rem', fontWeight: 600,
      }}>Watch engagement</Typography>
      <Grid container spacing={1} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="Recipients who watched"
            value={s.recipients_viewed || 0}
            hint={`${s.watch_sessions_total || 0} total session${(s.watch_sessions_total || 0) === 1 ? '' : 's'}`}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="Watched to completion"
            value={s.completions_at_95pct || 0}
            hint="sessions ≥95% through"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="Avg watch time"
            value={(() => {
              const sec = s.avg_watch_seconds || 0;
              if (sec < 60) return `${sec}s`;
              const m = Math.floor(sec / 60);
              const r = sec % 60;
              return `${m}m ${r.toString().padStart(2, '0')}s`;
            })()}
            hint="per session"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="Avg completion"
            value={`${Math.round(s.avg_completion_percent || 0)}%`}
            hint="across all sessions"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="Total watch time"
            value={(() => {
              const h = s.total_watch_hours || 0;
              if (h >= 1) return `${h}h`;
              const m = Math.round(h * 60);
              return `${m}m`;
            })()}
            hint="aggregate across recipients"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatTile
            label="Filmmaker comments"
            value={s.filmmaker_comments_total || 0}
            hint="notes from recipients"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Typography variant="caption" sx={{
            display: 'block', mb: 1, color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: 0.4,
            fontSize: '0.7rem', fontWeight: 600,
          }}>
            Activity timeline ({campaign.event_total || 0} events)
          </Typography>
          <Paper variant="outlined" sx={{ maxHeight: 600, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { py: 0.25, fontSize: '0.65rem', textTransform: 'none' } }}>
                  <TableCell>When</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(campaign.events || []).map((ev) => (
                  <TableRow key={ev.id} sx={{ '& td': { py: 0.5, fontSize: '0.72rem', verticalAlign: 'top' } }}>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                      {ev.occurred_at ? fmtDateTime(ev.occurred_at) : '—'}
                    </TableCell>
                    <TableCell>
                      <Box component="span" sx={{ color: eventColor(ev.event_type), fontWeight: 500 }}>
                        {eventLabel(ev.event_type)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {payloadSummary(ev)}
                    </TableCell>
                  </TableRow>
                ))}
                {(!campaign.events || campaign.events.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', color: 'text.disabled', py: 2 }}>
                      No events yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption" sx={{
            display: 'block', mb: 1, color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: 0.4,
            fontSize: '0.7rem', fontWeight: 600,
          }}>
            DCPs ({(campaign.dcps || []).length})
          </Typography>
          <Paper variant="outlined" sx={{ p: 1, mb: 2, maxHeight: 300, overflow: 'auto' }}>
            {(campaign.dcps || []).map((d) => (
              <Box key={d.id} sx={{ py: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.72rem', flex: 1 }}>
                  {d.name}
                </Typography>
                <Chip size="small" label={d.status}
                  color={d.status === 'completed' ? 'success' : 'default'}
                  variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
              </Box>
            ))}
            {(campaign.dcps || []).length === 0 && (
              <Typography variant="caption" color="text.disabled">No DCPs bound.</Typography>
            )}
          </Paper>

          <Typography variant="caption" sx={{
            display: 'block', mb: 1, color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: 0.4,
            fontSize: '0.7rem', fontWeight: 600,
          }}>
            Watermark screeners ({(campaign.watermark_jobs || []).length})
          </Typography>
          <Paper variant="outlined" sx={{ p: 1, maxHeight: 360, overflow: 'auto' }}>
            {(campaign.watermark_jobs || []).map((j) => (
              <Box key={j.id} sx={{ py: 0.25 }}>
                <Typography variant="caption" sx={{ fontSize: '0.72rem', display: 'block' }}>
                  {j.recipient_name || j.recipient_email || `Job #${j.id}`}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', display: 'block' }}>
                  {j.source_filename} · {j.completed_at ? fmtDate(j.completed_at) : 'pending'}
                </Typography>
              </Box>
            ))}
            {(campaign.watermark_jobs || []).length === 0 && (
              <Typography variant="caption" color="text.disabled">No screeners bound.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
      <CampaignEditDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        campaign={campaign}
        onSaved={() => load()}
      />
    </Box>
  );
}
